import {
  BarChart3,
  Rss,
  Brain,
  Archive,
  Settings,
  CreditCard,
  User,
  Download,
  Info,
  DollarSign
} from 'lucide-react'
import type { SubscriptionTier } from '@/types'
import { BriefingStackIcon } from '@/components/nav-icons/BriefingStackIcon'
import { DashIcon } from '@/components/nav-icons/DashIcon'
import { ContentIcon } from '@/components/nav-icons/ContentIcon'
export interface NavItem {
  key: string
  label: string
  href: string
  icon: any // Lucide icon component
  requiredTier?: SubscriptionTier[]
  external?: boolean
  badge?: string | number
  description?: string
}

export interface DropdownItem {
  key: string
  label: string
  href?: string
  icon: any
  action?: () => void
  divider?: boolean
  description?: string
  requiredTier?: SubscriptionTier[]
}

// Unified type for mobile menu items
export interface MobileMenuItem {
  key: string
  label: string
  href?: string
  icon: any
  requiredTier?: SubscriptionTier[]
  external?: boolean
  badge?: string | number
  description?: string
  divider?: boolean
  action?: () => void
}

// Public navigation (landing page)
export const publicNavItems: NavItem[] = [
  {
    key: 'features',
    label: 'Features',
    href: '/features',
    icon: null,
  },
  {
    key: 'pricing',
    label: 'Pricing',
    href: '/pricing',
    icon: null,
  },
  {
    key: 'about',
    label: 'About',
    href: '/about',
    icon: null,
  },
]

// Authenticated navigation (main app)
export const authenticatedNavItems: NavItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    href: '/dashboard',
    icon: DashIcon,
    description: 'Main briefing overview',
  },
  // {
  //   key: 'sources',
  //   label: 'Sources',
  //   href: '/sources',
  //   icon: ContentIcon,
  //   description: 'RSS feeds and source management',
  // },
  {
    key: 'intelligence',
    label: 'Intelligence',
    href: '/intelligence',
    icon: Brain,
    requiredTier: ['pro', 'enterprise'],
    description: 'Community insights and trends',
  },

]

// Settings dropdown items
export const settingsDropdownItems: DropdownItem[] = [
  {
    key: 'briefing-preferences',
    label: 'Briefing Preferences',
    href: '/settings/briefing',
    icon: Settings,
    description: 'Customize your briefings',
  },
  {
    key: 'api-advanced',
    label: 'API & Advanced',
    href: '/settings/api',
    icon: Settings,
    description: 'LLM provider and advanced settings',
  },
  {
    key: 'billing-usage',
    label: 'Billing & Usage',
    href: '/billing',
    icon: CreditCard,
    description: 'Manage subscription and usage',
  },
  {
    key: 'privacy-security',
    label: 'Privacy & Security',
    href: '/settings/privacy',
    icon: Settings,
    description: 'Privacy and security settings',
  },
]

// User dropdown items
export const userDropdownItems: DropdownItem[] = [
  {
    key: 'profile',
    label: 'Profile Settings',
    href: '/settings/profile',
    icon: User,
    description: 'Manage your profile',
  },
  {
    key: 'family-sharing',
    label: 'Family Sharing',
    href: '/settings/family',
    icon: User,
    requiredTier: ['pro', 'enterprise'],
    description: 'Share with family members',
  },
  {
    key: 'usage-analytics',
    label: 'Usage Analytics',
    href: '/settings/analytics',
    icon: BarChart3,
    description: 'View your usage statistics',
  },
  {
    key: 'export-data',
    label: 'Export All Data',
    href: '/settings/export',
    icon: Download,
    description: 'Export your data',
  },
  {
    key: 'help-support',
    label: 'Help & Support',
    href: '/help',
    icon: Info,
    description: 'Get help and support',
  },
  {
    key: 'divider-1',
    label: '',
    icon: null,
    divider: true,
  },
  {
    key: 'sign-out',
    label: 'Sign Out',
    icon: null,
    action: () => { }, // Will be passed from component
    description: 'Sign out of your account',
  },
]

// Mobile menu items (combined nav + settings for authenticated users)
export const mobileMenuItems = (isAuthenticated: boolean, userTier: SubscriptionTier = 'free'): MobileMenuItem[] => {
  if (!isAuthenticated) {
    return [
      ...publicNavItems.map(item => ({ ...item, href: item.href || '' })),
      {
        key: 'sign-in',
        label: 'Sign In',
        href: '/auth/login',
        icon: User,
      },
      {
        key: 'get-started',
        label: 'Get Started',
        href: '/auth/register',
        icon: User,
      },
    ]
  }

  return [
    ...authenticatedNavItems.filter(item =>
      !item.requiredTier || item.requiredTier.includes(userTier)
    ).map(item => ({ ...item })),
    {
      key: 'divider-1',
      label: '',
      href: '',
      icon: null,
      divider: true,
    },
    ...settingsDropdownItems.map(item => ({ ...item, href: item.href || '' })),
    {
      key: 'divider-2',
      label: '',
      href: '',
      icon: null,
      divider: true,
    },
    {
      key: 'upgrade',
      label: userTier === 'free' ? 'Upgrade to Pro' : 'Billing',
      href: '/billing',
      icon: CreditCard,
    },
    {
      key: 'sign-out',
      label: 'Sign Out',
      href: '',
      icon: null,
      action: () => { }, // Will be passed from component
    },
  ]
}

// Helper function to check if user has access to a nav item
export const hasAccess = (item: NavItem, userTier: SubscriptionTier): boolean => {
  if (!item.requiredTier) return true
  return item.requiredTier.includes(userTier)
}

// Tier display configuration
export const tierConfig = {
  free: {
    label: 'Free',
    icon: '🆓',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
  starter: {
    label: 'Starter',
    icon: '🚀',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  pro: {
    label: 'Pro',
    icon: '💎',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  team: {
    label: 'Team',
    icon: '👥',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  enterprise: {
    label: 'Enterprise',
    icon: '📊',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
}